/**
 * features/admin/BulkImport.tsx
 * CSV student import. Columns: FullName, Class, Gender, AdmissionNumber,
 * ParentEmail, ParentWhatsApp (the last two power feature #5/#6).
 */
import { useState, useRef } from 'react';
import { collection, writeBatch, doc } from 'firebase/firestore';
import {
  Upload,
  Download,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  sanitizeText,
  sanitizeEmail,
  sanitizePhone,
} from '@/lib/sanitize';
import { Card, PageHeader, Button } from '@/components/ui/primitives';

interface ParsedRow {
  fullName: string;
  currentClass: string;
  gender: 'M' | 'F';
  admissionNumber: string;
  parentEmail: string;
  parentWhatsapp: string;
  error?: string;
}

export default function BulkImport() {
  const { classes } = useData();
  const { userData } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const classNames = new Map(
    classes.map((c) => [c.name.toLowerCase().replace(/\s/g, ''), c.id]),
  );

  function template() {
    const csv =
      'FullName,Class,Gender,AdmissionNumber,ParentEmail,ParentWhatsApp\n' +
      'Thabo Moeng,Form 3A,M,LKC20001,parent1@example.com,+2677XXXXXXX\n' +
      'Naledi Tau,Form 3A,F,LKC20002,parent2@example.com,+2677XXXXXXX';
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'lkc_student_import_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function parse(text: string) {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length < 2) {
      setMsg({ ok: false, text: 'CSV needs a header and at least one row.' });
      return;
    }
    const headers = lines[0]
      .split(',')
      .map((h) => h.trim().toLowerCase().replace(/['"]/g, ''));
    const idx = (names: string[]) =>
      headers.findIndex((h) => names.some((n) => h.includes(n)));
    const iName = idx(['fullname', 'name']);
    const iClass = idx(['class']);
    const iGender = idx(['gender', 'sex']);
    const iAdm = idx(['admission', 'adm']);
    const iEmail = idx(['email']);
    const iWa = idx(['whatsapp', 'phone', 'mobile']);

    if (iName < 0 || iClass < 0) {
      setMsg({
        ok: false,
        text: 'CSV must have at least FullName and Class columns.',
      });
      return;
    }

    const parsed: ParsedRow[] = lines.slice(1).map((line) => {
      const cells = line.split(',').map((c) => c.trim());
      const fullName = sanitizeText(cells[iName] ?? '', 80);
      const rawClass = (cells[iClass] ?? '')
        .toLowerCase()
        .replace(/\s/g, '');
      const classId = classNames.get(rawClass) ?? '';
      const g = (cells[iGender] ?? 'M').toUpperCase().charAt(0);
      const row: ParsedRow = {
        fullName,
        currentClass: classId,
        gender: g === 'F' ? 'F' : 'M',
        admissionNumber: sanitizeText(
          iAdm >= 0 ? cells[iAdm] ?? '' : '',
          30,
        ),
        parentEmail:
          iEmail >= 0 ? sanitizeEmail(cells[iEmail] ?? '') : '',
        parentWhatsapp:
          iWa >= 0 ? sanitizePhone(cells[iWa] ?? '') : '',
      };
      if (!fullName) row.error = 'Missing name';
      else if (!classId)
        row.error = `Class "${cells[iClass]}" not found`;
      return row;
    });
    setRows(parsed);
    const bad = parsed.filter((r) => r.error).length;
    setMsg({
      ok: bad === 0,
      text: `Parsed ${parsed.length} rows${bad ? `, ${bad} with errors` : ' — all valid'}.`,
    });
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => parse(String(reader.result));
    reader.readAsText(f);
  }

  async function doImport() {
    const valid = rows.filter((r) => !r.error);
    if (valid.length === 0) {
      setMsg({ ok: false, text: 'No valid rows to import.' });
      return;
    }
    setImporting(true);
    try {
      let n = 0;
      let batch = writeBatch(db);
      for (const r of valid) {
        const ref = doc(collection(db, COLLECTIONS.students));
        batch.set(ref, {
          fullName: r.fullName,
          currentClass: r.currentClass,
          gender: r.gender,
          admissionNumber: r.admissionNumber,
          subjects: [],
          subjectTeachers: {},
          parentContact: {
            email: r.parentEmail,
            whatsapp: r.parentWhatsapp,
            consentToSend: false,
          },
          createdAt: new Date().toISOString(),
          createdBy: userData?.uid,
        });
        if (++n % 400 === 0) {
          await batch.commit();
          batch = writeBatch(db);
        }
      }
      await batch.commit();
      auditFromContext(userData, 'students_bulk_imported', 'admissions', {
        targetType: 'student', details: { count: valid.length },
      });
      setMsg({
        ok: true,
        text: `Imported ${valid.length} students. Assign subjects in Students setup.`,
      });
      setRows([]);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setImporting(false);
    }
  }

  const validCount = rows.filter((r) => !r.error).length;

  return (
    <div>
      <PageHeader
        title="Bulk Import"
        subtitle="Import students from a CSV file"
        actions={
          <Button
            variant="secondary"
            icon={<Download size={15} />}
            onClick={template}
          >
            Download template
          </Button>
        }
      />

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}
        >
          {msg.text}
        </div>
      )}

      <Card className="p-6 mb-4">
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          onChange={onFile}
          className="hidden"
        />
        <div
          onClick={() => fileRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 text-center cursor-pointer hover:border-primary transition-colors"
        >
          <Upload
            size={28}
            className="mx-auto text-gray-400 mb-2"
          />
          <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
            Click to choose a CSV file
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Columns: FullName, Class, Gender, AdmissionNumber,
            ParentEmail, ParentWhatsApp
          </p>
        </div>
      </Card>

      {rows.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {validCount} valid / {rows.length} rows
            </span>
            <Button
              icon={
                importing ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <FileText size={15} />
                )
              }
              disabled={importing || validCount === 0}
              onClick={doImport}
            >
              Import {validCount} students
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Class</th>
                <th className="px-4 py-2 font-medium">Gender</th>
                <th className="px-4 py-2 font-medium">Parent email</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.slice(0, 100).map((r, i) => (
                <tr key={i}>
                  <td className="px-4 py-2">{r.fullName || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">
                    {classes.find((c) => c.id === r.currentClass)
                      ?.name ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-gray-500">
                    {r.gender}
                  </td>
                  <td className="px-4 py-2 text-gray-500">
                    {r.parentEmail || '—'}
                  </td>
                  <td className="px-4 py-2">
                    {r.error ? (
                      <span className="text-red-500 text-xs flex items-center gap-1">
                        <AlertTriangle size={12} />
                        {r.error}
                      </span>
                    ) : (
                      <span className="text-emerald-600 text-xs flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        OK
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 100 && (
            <p className="p-3 text-xs text-gray-400 text-center">
              Showing first 100 of {rows.length} rows.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
